import React, { useContext, useEffect, useState } from 'react';

import { Product, Stock, TypeService, Units } from '../../models/interfaces';
import { DeletedStock, StocksContext } from '../../context/stock/StockContext';
import { DeletedStockNotice } from '../stock/DeletedStockNotice';
import { CategoriesContext } from '../../context/category/CategoryContext';
import { SuppliersContext } from '../../context/supplier/SupplierContext';
import { CombosContext } from '../../context/combo/ComboContext';


// Valor que usa el formulario de producto para "cantidad sin definir"
const UNLIMITED_AVAILABLE = 1000000;

interface ProductStockModalProps {
    product: Product;
    onClose: () => void;
    onSaved: (message: string) => void;
    onManageExisting: () => void;   // ir a la pantalla actual para asociar productos existentes
}

// Crea el producto en el inventario a partir de un producto del menú y lo asocia a él
export const ProductStockModal = ({ product, onClose, onSaved, onManageExisting }: ProductStockModalProps) => {

    const { createStock, loadUnits } = useContext(StocksContext);
    const { categories, loadCategories, createCategory } = useContext(CategoriesContext);
    const { suppliers, loadSuppliers, addSupplier, loadTypeService } = useContext(SuppliersContext);
    const { addCombo } = useContext(CombosContext);

    const [units, setUnits] = useState<Units[]>([]);
    const [serviceTypes, setServiceTypes] = useState<TypeService[]>([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(true);

    // Datos del producto de inventario (precargados desde el producto)
    const available = Number(product.available);
    const [form, setForm] = useState({
        code: String(product.codeId ?? ''),
        name: product.name ?? '',
        newStock: !isNaN(available) && available < UNLIMITED_AVAILABLE ? String(available) : '',
        unit: product.unit ? String(product.unit) : '-1',
        categoryId: '-1',
        supplierId: '-1',
        minimunStock: '',
        cost: '',
        comboQuantity: '1',
    });

    // Alta rápida de categoría y proveedor
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [savingCategory, setSavingCategory] = useState(false);

    const [showNewSupplier, setShowNewSupplier] = useState(false);
    const [newSupplier, setNewSupplier] = useState({ name: '', serviceTypeId: '-1', phone: '', email: '' });
    const [savingSupplier, setSavingSupplier] = useState(false);

    // Producto eliminado con el mismo código: se reactiva por defecto
    const [deletedMatch, setDeletedMatch] = useState<DeletedStock | null>(null);
    const [reactivate, setReactivate] = useState(true);

    const [submitted, setSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([
            loadUnits().then(u => setUnits(u ?? [])).catch(() => setUnits([])),
            loadCategories().catch(() => { }),
            loadSuppliers(0).catch(() => { }),
        ]).finally(() => setLoadingCatalogs(false));
    }, []);

    const setField = (field: keyof typeof form, value: string) => setForm(prev => ({ ...prev, [field]: value }));

    const unitName = units.find(u => String(u.id) === form.unit)?.description ?? '';

    // Validaciones (se muestran después del primer intento de guardar)
    const errors = {
        code: !form.code.trim(),
        name: !form.name.trim(),
        categoryId: form.categoryId === '-1',
        supplierId: form.supplierId === '-1',
        unit: form.unit === '-1',
        newStock: form.newStock === '' || isNaN(Number(form.newStock)) || Number(form.newStock) < 0,
        comboQuantity: form.comboQuantity === '' || isNaN(Number(form.comboQuantity)) || Number(form.comboQuantity) <= 0,
        minimunStock: form.minimunStock !== '' && (isNaN(Number(form.minimunStock)) || Number(form.minimunStock) < 0),
        cost: form.cost !== '' && (isNaN(Number(form.cost)) || Number(form.cost) < 0),
    };
    const isValid = !Object.values(errors).some(Boolean);
    const invalid = (field: keyof typeof errors) => submitted && errors[field] ? 'is-invalid' : '';

    const handleCreateCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) return;
        setSavingCategory(true);
        setError('');
        const resp = await createCategory({ id: undefined, name } as any, (category) => setField('categoryId', String(category.id)));
        setSavingCategory(false);
        if (resp === 'success') {
            setShowNewCategory(false);
            setNewCategoryName('');
        } else {
            setError(resp);
        }
    };

    const openNewSupplier = () => {
        setShowNewSupplier(true);
        if (serviceTypes.length === 0) {
            loadTypeService().then(types => setServiceTypes(types ?? [])).catch(() => setServiceTypes([]));
        }
    };

    const handleCreateSupplier = async () => {
        if (!newSupplier.name.trim() || newSupplier.serviceTypeId === '-1') return;
        setSavingSupplier(true);
        setError('');
        const resp = await addSupplier({
            name: newSupplier.name.trim(),
            serviceTypeId: Number(newSupplier.serviceTypeId),
            phone: newSupplier.phone.trim(),
            email: newSupplier.email.trim(),
            legalId: '',
            mobile: '',
            address: '',
            contact: '',
        } as any, (supplier) => setField('supplierId', String(supplier.supplierCode)));
        setSavingSupplier(false);
        if (resp === 'success') {
            setShowNewSupplier(false);
            setNewSupplier({ name: '', serviceTypeId: '-1', phone: '', email: '' });
        } else {
            setError(resp);
        }
    };

    const handleSave = async () => {
        setSubmitted(true);
        setError('');
        if (!isValid) return;


        setIsSaving(true);

        // 1. Crear el producto en el inventario (o reactivar el eliminado con ese código)
        let created: Stock | null = null;
        let wasReactivated = false;
        const stockResp = await createStock({
            code: form.code.trim(),
            name: form.name.trim(),
            categoryId: Number(form.categoryId),
            unit: form.unit,
            newStock: Number(form.newStock),
            supplierId: Number(form.supplierId),
            minimunStock: form.minimunStock === '' ? 0 : Number(form.minimunStock),
            cost: form.cost === '' ? 0 : Number(form.cost),
            currency: 840,
            reactivate,
        } as any, (stock, reactivated) => { created = stock; wasReactivated = reactivated; });

        if (stockResp !== 'success' || !created) {
            setIsSaving(false);
            setError(stockResp !== 'success' ? stockResp : 'No se pudo crear el producto en el inventario.');
            return;
        }

        // 2. Asociarlo al producto del menú (igual que en "Asociar inventario")
        const comboResp = await addCombo({
            productId: product.id,
            stockId: (created as Stock).id,
            quantity: form.comboQuantity,
        } as any);

        setIsSaving(false);

        if (comboResp !== 'success') {
            setError(`El producto se creó en el inventario, pero no se pudo asociar: ${comboResp}. Puede asociarlo desde "Asociar un producto existente".`);
            return;
        }

        onSaved(wasReactivated
            ? `"${form.name.trim()}" se reactivó en el inventario (con su historial) y quedó asociado a ${product.name}.`
            : `"${form.name.trim()}" se agregó al inventario y quedó asociado a ${product.name}.`);
    };

    const busy = isSaving || savingCategory || savingSupplier;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => !busy && onClose()}>
            <div className="modal-dialog modal-dialog-scrollable modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content border-0">

                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title mb-0">Asociar inventario</h5>
                            <div className="text-muted small">Cree el producto en el inventario y asócielo a <span className="fw-semibold text-body">{product.name}</span>.</div>
                        </div>
                        <button type="button" className="btn-close" onClick={onClose} disabled={busy} />
                    </div>

                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                                <i className="bi bi-exclamation-circle mt-1" />
                                <div className="small">{error}</div>
                            </div>
                        )}

                        {loadingCatalogs ? (
                            <div className="text-center py-4">
                                <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                            </div>
                        ) : (
                            <div className="row g-3">
                                {/* Datos del producto */}
                                <div className="col-md-4">
                                    <label className="form-label small text-muted mb-1">Código *</label>
                                    <input className={`form-control ${invalid('code')}`} value={form.code} onChange={(e) => setField('code', e.target.value)} placeholder="Código" />
                                    <div className="invalid-feedback">El código es requerido.</div>
                                </div>
                                <div className="col-md-8">
                                    <label className="form-label small text-muted mb-1">Nombre *</label>
                                    <input className={`form-control ${invalid('name')}`} value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Nombre" />
                                    <div className="invalid-feedback">El nombre es requerido.</div>
                                </div>

                                <div className={deletedMatch ? 'col-12' : 'd-none'}>
                                    <DeletedStockNotice
                                        code={form.code}
                                        reactivate={reactivate}
                                        onReactivateChange={setReactivate}
                                        onMatchChange={setDeletedMatch}
                                        disabled={busy}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">{deletedMatch && reactivate ? 'Cantidad que ingresa *' : 'Cantidad inicial *'}</label>
                                    <input type="number" min={0} className={`form-control ${invalid('newStock')}`} value={form.newStock} onChange={(e) => setField('newStock', e.target.value)} placeholder="Cantidad en stock" />
                                    <div className="invalid-feedback">Ingrese una cantidad válida (0 o más).</div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">Unidad de medida *</label>
                                    <select className={`form-select ${invalid('unit')}`} value={form.unit} onChange={(e) => setField('unit', e.target.value)}>
                                        <option value="-1">Seleccione una opción</option>
                                        {units.map(u => <option key={u.id} value={u.id}>{u.description}</option>)}
                                    </select>
                                    <div className="invalid-feedback">Seleccione la unidad de medida.</div>
                                </div>

                                {/* Categoría */}
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">Categoría *</label>
                                    <div className="input-group has-validation">
                                        <select className={`form-select ${invalid('categoryId')}`} value={form.categoryId} onChange={(e) => setField('categoryId', e.target.value)}>
                                            <option value="-1">Seleccione una opción</option>
                                            {categories.map(c => <option selected={c.name == "Sin categoría"} key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                        <button type="button" className="btn btn-outline-secondary" title="Nueva categoría" onClick={() => setShowNewCategory(v => !v)} disabled={busy}>
                                            <i className={`bi ${showNewCategory ? 'bi-x-lg' : 'bi-plus-lg'}`} />
                                        </button>
                                        <div className="invalid-feedback">Seleccione o cree una categoría.</div>
                                    </div>
                                    {showNewCategory && (
                                        <div className="border rounded p-2 mt-2 bg-light">
                                            <div className="small fw-medium mb-1">Nueva categoría</div>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    className="form-control"
                                                    placeholder="Nombre de la categoría"
                                                    value={newCategoryName}
                                                    autoFocus
                                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); } }}
                                                />
                                                <button type="button" className="btn btn-primary" onClick={handleCreateCategory} disabled={!newCategoryName.trim() || savingCategory}>
                                                    {savingCategory ? <span className="spinner-border spinner-border-sm" /> : 'Agregar'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Proveedor */}
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">Proveedor *</label>
                                    <div className="input-group has-validation">
                                        <select className={`form-select ${invalid('supplierId')}`} value={form.supplierId} onChange={(e) => setField('supplierId', e.target.value)}>
                                            <option value="-1">Seleccione una opción</option>
                                            {suppliers.map(s => <option key={s.supplierCode} value={s.supplierCode}>{s.name}</option>)}
                                        </select>
                                        <button type="button" className="btn btn-outline-secondary" title="Nuevo proveedor" onClick={() => showNewSupplier ? setShowNewSupplier(false) : openNewSupplier()} disabled={busy}>
                                            <i className={`bi ${showNewSupplier ? 'bi-x-lg' : 'bi-plus-lg'}`} />
                                        </button>
                                        <div className="invalid-feedback">Seleccione o cree un proveedor.</div>
                                    </div>
                                    {showNewSupplier && (
                                        <div className="border rounded p-2 mt-2 bg-light">
                                            <div className="small fw-medium mb-2">Nuevo proveedor</div>
                                            <div className="row g-2">
                                                <div className="col-12">
                                                    <input className="form-control form-control-sm" placeholder="Nombre *" value={newSupplier.name} autoFocus
                                                        onChange={(e) => setNewSupplier(prev => ({ ...prev, name: e.target.value }))} />
                                                </div>
                                                <div className="col-12">
                                                    <select className="form-select form-select-sm" value={newSupplier.serviceTypeId}
                                                        onChange={(e) => setNewSupplier(prev => ({ ...prev, serviceTypeId: e.target.value }))}>
                                                        <option value="-1">Tipo de servicio *</option>
                                                        {serviceTypes.map(t => <option key={t.id} value={t.id}>{t.description}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-6">
                                                    <input className="form-control form-control-sm" placeholder="Teléfono" value={newSupplier.phone}
                                                        onChange={(e) => setNewSupplier(prev => ({ ...prev, phone: e.target.value }))} />
                                                </div>
                                                <div className="col-6">
                                                    <input type="email" className="form-control form-control-sm" placeholder="Email" value={newSupplier.email}
                                                        onChange={(e) => setNewSupplier(prev => ({ ...prev, email: e.target.value }))} />
                                                </div>
                                                <div className="col-12 d-grid">
                                                    <button type="button" className="btn btn-sm btn-primary" onClick={handleCreateSupplier}
                                                        disabled={!newSupplier.name.trim() || newSupplier.serviceTypeId === '-1' || savingSupplier}>
                                                        {savingSupplier ? <span className="spinner-border spinner-border-sm" /> : 'Agregar proveedor'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">Cantidad mínima</label>
                                    <input type="number" min={0} className={`form-control ${invalid('minimunStock')}`} value={form.minimunStock} onChange={(e) => setField('minimunStock', e.target.value)} placeholder="0" />
                                    <div className="form-text">Se alertará cuando el stock llegue a esta cantidad.</div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">Costo</label>
                                    <div className="input-group has-validation">
                                        <span className="input-group-text">$</span>
                                        <input type="number" min={0} step="0.01" className={`form-control ${invalid('cost')}`} value={form.cost} onChange={(e) => setField('cost', e.target.value)} placeholder="0.00" />
                                        <div className="invalid-feedback">Ingrese un costo válido.</div>
                                    </div>
                                </div>

                                {/* Asociación */}
                                <div className="col-12">
                                    <div className="border rounded p-3 bg-light">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <i className="bi bi-link-45deg text-primary" />
                                            <span className="fw-medium">Asociación</span>
                                        </div>
                                        <label className="form-label small text-muted mb-1">Cantidad a descontar del inventario por cada venta *</label>
                                        <div className="input-group has-validation" style={{ maxWidth: 320 }}>
                                            <input type="number" min={0} step="any" className={`form-control ${invalid('comboQuantity')}`} value={form.comboQuantity} onChange={(e) => setField('comboQuantity', e.target.value)} />
                                            {unitName && <span className="input-group-text">{unitName}</span>}
                                            <div className="invalid-feedback">Ingrese una cantidad mayor a 0.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        {/* <button type="button" className="btn btn-link text-decoration-none me-auto px-0" onClick={onManageExisting} disabled={busy}>
                            <i className="bi bi-box-seam me-1" /> Asociar un producto existente
                        </button> */}
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy || loadingCatalogs}>
                            {isSaving
                                ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</>
                                : <><i className="bi bi-check-lg me-1" /> Guardar</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
